// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import type { ChatImageBlock } from './chatbotTypes';

// Pano vision: capture only the CURRENT view (the portion the user is looking at), not the whole 360°.
// Pannellum's WebGL canvas can't be reliably snapshotted (no preserveDrawingBuffer), so instead we crop the
// source equirectangular image — which we CAN fetch — to the current yaw/pitch/hfov. The viewer fetches the
// equirect once and registers a synchronous capturer; the chatbot calls it at SEND so the frame is exactly
// what's on screen. A module singleton (not Redux) because the capture is synchronous and its base64 payload
// should not live in the store.

const MAX_EDGE_PX = 1568; // Anthropic downscales beyond this anyway; keep the crop small + under the size cap.
const clamp = (value: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, value));

/**
 * Crop a fetched equirectangular pano to the current view and return it as a base64 ChatImageBlock.
 * Convention (pannellum default, haov 360 / vaov 180): yaw 0° = image horizontal centre, +yaw pans right;
 * pitch 0° = vertical centre, +pitch = up. `aspect` is the viewport width/height (sets the vertical FOV).
 * Returns null on any failure so a chat turn never breaks.
 */
export const cropEquirectToView = (
  bitmap: ImageBitmap,
  yaw: number,
  pitch: number,
  hfov: number,
  aspect: number,
): ChatImageBlock | null => {
  try {
    const imgW = bitmap.width;
    const imgH = bitmap.height;
    const hfovDeg = clamp(hfov || 100, 30, 120);
    const vfovDeg = clamp(hfovDeg / (aspect > 0 ? aspect : 1.5), 20, 150);

    const cropW = Math.max(1, Math.round((imgW * hfovDeg) / 360));
    const cropH = Math.max(1, Math.round((imgH * vfovDeg) / 180));
    const centerX = imgW * (0.5 + yaw / 360);
    const centerY = imgH * (0.5 - pitch / 180);
    const srcX = (((Math.round(centerX - cropW / 2) % imgW) + imgW) % imgW); // wrap at the equirect seam
    const srcY = clamp(Math.round(centerY - cropH / 2), 0, Math.max(0, imgH - cropH));

    const scale = Math.min(1, MAX_EDGE_PX / Math.max(cropW, cropH));
    const outW = Math.max(1, Math.round(cropW * scale));
    const outH = Math.max(1, Math.round(cropH * scale));
    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    if (srcX + cropW <= imgW) {
      ctx.drawImage(bitmap, srcX, srcY, cropW, cropH, 0, 0, outW, outH);
    } else {
      // The crop straddles the seam: draw the right-of-seam slice, then wrap the remainder from x=0.
      const firstW = imgW - srcX;
      const firstOut = Math.round(firstW * scale);
      ctx.drawImage(bitmap, srcX, srcY, firstW, cropH, 0, 0, firstOut, outH);
      ctx.drawImage(bitmap, 0, srcY, cropW - firstW, cropH, firstOut, 0, outW - firstOut, outH);
    }

    const data = canvas.toDataURL('image/jpeg', 0.85).split(',', 2)[1];
    return data ? { type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data } } : null;
  } catch {
    return null;
  }
};

// Where the camera is and which way it's looking, so Khora can resolve WHICH pole is in the current view
// (the pole the red view-cone covers) instead of guessing its identity from the photo. bearing_deg is a
// world heading (0=N, 90=E) = pano kappa + pannellum yaw — the same math that aims the on-map cone.
export interface PanoViewContext {
  lon: number;
  lat: number;
  bearing_deg: number;
  hfov_deg: number;
}

/** One pano-vision capture: the current-view image plus the view geometry that grounds it to a pole. */
export interface PanoCapture {
  image: ChatImageBlock;
  view: PanoViewContext | null;
}

// ---- viewer → chatbot capture bridge (module singleton) ---- //
let capturer: (() => PanoCapture | null) | null = null;

/** The pano viewer registers (or clears with null) the function that crops its current view + view geometry. */
export const registerPanoCapturer = (fn: (() => PanoCapture | null) | null): void => {
  capturer = fn;
};

/** The chatbot calls this at send time; returns the current-view image + view geometry, or null when pano vision is off. */
export const captureCurrentPanoFrame = (): PanoCapture | null => {
  try {
    return capturer ? capturer() : null;
  } catch {
    return null;
  }
};
